import CryptoJS, { AES } from 'crypto-js';
import { isAppOnLocal, iv, key } from './url';

/**
 * Initialization vector for cryptographic operations, parsed from hexadecimal.
 */
const cryptoIV = CryptoJS.enc.Hex.parse(iv);

/**
 * Encrypts a string using AES encryption.
 * @param {string} data - The string to be encrypted.
 * @returns {string} The encrypted string.
 */
export const encrypt = (data: string) =>
  AES.encrypt(data, key, {
    iv: cryptoIV,
  }).toString();

/**
 * Decrypts an encrypted string using AES decryption.
 * @param {string} data - The encrypted string to be decrypted.
 * @returns {string} The decrypted string.
 */
export const decrypt = (data: string) => {
  try {
    return AES.decrypt(data, key, {
      iv: cryptoIV,
    }).toString(CryptoJS.enc.Utf8);
  } catch (err) {
    return '';
  }
};
/**
 * Encrypts an object by stringifying it and then encrypting the resulting string.
 * @param {object} data - The object to be encrypted.
 * @returns {string} The encrypted string representation of the object.
 */
export const encryptObject = (data: object) => {
  const stringData = JSON.stringify(data);
  const encrypted = CryptoJS.AES.encrypt(stringData, key, {
    iv: cryptoIV,
  }).toString();
  return encrypted;
};

/**
 * Decrypts an encrypted string and parses it back into an object.
 * @param {string} encryptedData - The encrypted string to be decrypted and parsed.
 * @returns {object} The decrypted and parsed object.
 */
export const decryptObject = (encryptedData: string) => {
  const decrypted = CryptoJS.AES.decrypt(encryptedData, key, {
    iv: cryptoIV,
  }).toString(CryptoJS.enc.Utf8);
  const parsedData = JSON.parse(decrypted);
  return parsedData;
};

/**
 * Encrypts a payload object, with special handling for local environment and fetch API.
 * @param {object} data - The object to be encrypted.
 * @param {boolean} [isFetchApi=false] - Flag indicating if the encryption is for fetch API.
 * @returns {string|object} The encrypted payload or the original data, depending on conditions.
 */
export const encryptPayload = (data: object, isFetchApi: boolean = false) => {
  const stringData = JSON.stringify(data);
  if (isAppOnLocal || true) {
    if (isFetchApi) {
      return stringData;
    }
    return data;
  }
  const encrypted = CryptoJS.AES.encrypt(stringData, key, {
    iv: cryptoIV,
  }).toString();

  return JSON.stringify({
    encrypted,
  });
};

/**
 * Hashes a name using SHA-256 algorithm.
 * @param {string} name - The name to be hashed.
 * @returns {string} The hashed name as a hexadecimal string.
 */
export const hashName = (name: string) =>
  CryptoJS.SHA256(name).toString(CryptoJS.enc.Hex);

export const hashSignature = (details: string) =>
  CryptoJS.SHA512(details).toString();

export const stableStringify = (value: unknown) =>
  JSON.stringify(value, (_key, val) => {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      return Object.keys(val as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((acc, k) => {
          acc[k] = (val as Record<string, unknown>)[k];
          return acc;
        }, {});
    }
    return val;
  });

/**
 * Decrypts a JSON string and parses it into an object.
 * @param {string} data - The encrypted JSON string to be decrypted and parsed.
 * @returns {object} The decrypted and parsed object.
 */
export const decryptJSON = (data: string) => JSON?.parse(decrypt(data));

export const hashData = (data: any) => {
  const jsonString = JSON.stringify(data);
  return CryptoJS.MD5(jsonString).toString(CryptoJS.enc.Hex);
};

const clearIndexedDB = async (): Promise<void> => {
  if (!('indexedDB' in window)) return;

  try {
    const databases = await indexedDB.databases();

    await Promise.all(
      databases.map((db) => {
        if (!db.name) return Promise.resolve();
        return new Promise((resolve, reject) => {
          const request = indexedDB.deleteDatabase(db.name!);

          request.onsuccess = () => resolve(true);
          request.onerror = () => reject(request.error);
          request.onblocked = () => resolve(false);
        });
      }),
    );
  } catch (err) {
    console.error('Failed to clear IndexedDB:', err);
  }
};

export const clearAllBrowserData = async () => {
  const { origin } = window.location;

  const allowed = [
    'https://xeniacloudpms.servoit.solutions',
    'https://xeniacloud.servoit.solutions',
    'https://uat.servoit.solutions',
    'http://127.0.0.1:5173',
  ];

  if (!allowed.includes(origin)) return;

  // Storage
  localStorage.clear();
  sessionStorage.clear();

  // Cookies
  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim();
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`;
  });

  // IndexedDB
  await clearIndexedDB();

  // Cache Storage
  if ('caches' in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  }
};
